/**
 * ButtonItem shape library — 12 shapes across 4 families.
 *
 * Single source of truth for:
 *   - aspect rules (square / free / min-ratio) — EditableItem reads these
 *   - per-shape size presets (S/M/L pixel dims) — replaces the flat entry
 *     in sizes.ts for the button kind
 *   - clip-path generation for polygon-family shapes — FireButton reads
 *   - CSS border-radius rules for rectangle/curve family — CSS reads
 *   - text padding (centroid inset) for asymmetric shapes — FireButton reads
 *
 * Rectangle/curve family (rect/pill/bar/circle/ellipse) renders via
 * border-radius on the button element. Polygon family (triangle/diamond/
 * pentagon/hexagon/octagon/arrow/chevron) renders via inline clip-path
 * generated from (w, h) each render — that also handles hit-area for free
 * (pointer events outside the clip don't fire).
 *
 * Rotation applies as CSS transform on the outer EditableItem wrapper.
 * Because clip-path is on the inner button and rotation is on the wrapper,
 * both visuals + hit-area move together under rotation.
 */

import type { ButtonShape } from './projectStore.ts';

export interface SizeDims { w: number; h: number }

export type AspectRule =
  | { kind: 'free' }
  | { kind: 'square' }
  | { kind: 'minRatio'; ratio: number }
  // Floor at square (1:1) but either axis may exceed the other. Dragging
  // the shorter axis below the longer one clamps at parity. Used by
  // `rect` — user intent 2026-07-03: rectangle grows landscape OR portrait
  // but a strip reads as "wrong shape entirely," go pick pill/bar for that.
  | { kind: 'squareFloor' };

export interface ShapeDef {
  id: ButtonShape;
  /** Display label for accessibility labels and tooltips. */
  label: string;
  aspect: AspectRule;
  /** S / M / L pixel presets. Freeform resize clamps to [S, L] per axis. */
  presets: { S: SizeDims; M: SizeDims; L: SizeDims };
  /** Family drives the render strategy (border-radius vs clip-path). */
  family: 'rect' | 'curve' | 'polygon';
  /** Optional text-slot inset (percentage of width/height) — for shapes
   *  where the geometric centroid differs from the bbox center (triangle,
   *  arrow, chevron). Absent = default centered padding. */
  textInset?: { top: number; right: number; bottom: number; left: number };
}

/** Standard square presets — used by every 1:1-locked shape. */
const SQUARE_PRESETS = {
  S: { w: 120, h: 120 },
  M: { w: 160, h: 160 },
  L: { w: 240, h: 240 },
};

export const SHAPE_LIBRARY: ShapeDef[] = [
  // ─── Rectangles ─────────────────────────────────────────────
  {
    id: 'rect',
    // 1:1 minimum aspect (square floor) — rectangle grows freely wider
    // OR taller than square but can't shrink to a strip. Below-square
    // reads as "wrong shape entirely" per user 2026-07-03; use `pill`
    // or `bar` for landscape strips.
    label: 'Rectangle',
    aspect: { kind: 'squareFloor' },
    presets: {
      S: { w: 120, h: 120 },
      M: { w: 160, h: 160 },
      L: { w: 240, h: 240 },
    },
    family: 'rect',
  },
  {
    id: 'pill',
    label: 'Pill',
    aspect: { kind: 'free' },
    presets: {
      S: { w: 160, h: 80 },
      M: { w: 220, h: 100 },
      L: { w: 320, h: 120 },
    },
    family: 'rect',
  },
  {
    id: 'bar',
    label: 'Bar',
    aspect: { kind: 'minRatio', ratio: 2 },
    presets: {
      S: { w: 240, h: 60 },
      M: { w: 320, h: 80 },
      L: { w: 480, h: 100 },
    },
    family: 'rect',
  },

  // ─── Curves ─────────────────────────────────────────────────
  {
    id: 'circle',
    label: 'Circle',
    aspect: { kind: 'square' },
    presets: SQUARE_PRESETS,
    family: 'curve',
  },
  {
    id: 'ellipse',
    label: 'Ellipse',
    aspect: { kind: 'free' },
    presets: {
      S: { w: 160, h: 100 },
      M: { w: 220, h: 140 },
      L: { w: 320, h: 200 },
    },
    family: 'curve',
  },

  // ─── Polygons ───────────────────────────────────────────────
  {
    id: 'triangle',
    label: 'Triangle',
    aspect: { kind: 'square' },
    presets: SQUARE_PRESETS,
    family: 'polygon',
    // Point at top → text sits toward the wider bottom third.
    textInset: { top: 45, right: 15, bottom: 10, left: 15 },
  },
  {
    id: 'diamond',
    label: 'Diamond',
    // Rhombus, taller than wide — distinct from a rotated square.
    aspect: { kind: 'square' },
    presets: {
      // Slightly taller — the ratio's baked into the clipPath, not the
      // bbox, so presets stay square for grid alignment.
      S: { w: 120, h: 120 },
      M: { w: 160, h: 160 },
      L: { w: 240, h: 240 },
    },
    family: 'polygon',
    textInset: { top: 25, right: 25, bottom: 25, left: 25 },
  },
  {
    id: 'pentagon',
    label: 'Pentagon',
    aspect: { kind: 'square' },
    presets: SQUARE_PRESETS,
    family: 'polygon',
    textInset: { top: 30, right: 15, bottom: 10, left: 15 },
  },
  {
    id: 'hexagon',
    label: 'Hexagon',
    aspect: { kind: 'square' },
    presets: SQUARE_PRESETS,
    family: 'polygon',
  },
  {
    id: 'octagon',
    label: 'Octagon',
    aspect: { kind: 'square' },
    presets: SQUARE_PRESETS,
    family: 'polygon',
  },

  // ─── Affordances ────────────────────────────────────────────
  {
    id: 'arrow',
    label: 'Arrow',
    aspect: { kind: 'free' },
    presets: {
      S: { w: 180, h: 100 },
      M: { w: 240, h: 130 },
      L: { w: 360, h: 200 },
    },
    family: 'polygon',
    // Points right → text hugs the flat left third of the shape.
    textInset: { top: 20, right: 40, bottom: 20, left: 10 },
  },
  {
    id: 'chevron',
    label: 'Chevron',
    aspect: { kind: 'free' },
    presets: {
      S: { w: 180, h: 100 },
      M: { w: 240, h: 130 },
      L: { w: 360, h: 200 },
    },
    family: 'polygon',
    textInset: { top: 20, right: 40, bottom: 20, left: 10 },
  },
];

/** Index for O(1) lookup. */
const SHAPE_INDEX: Record<ButtonShape, ShapeDef> = SHAPE_LIBRARY.reduce(
  (acc, s) => { acc[s.id] = s; return acc; },
  {} as Record<ButtonShape, ShapeDef>,
);

export function shapeDef(shape: ButtonShape): ShapeDef {
  return SHAPE_INDEX[shape];
}

export function shapeAspectRule(shape: ButtonShape): AspectRule {
  return SHAPE_INDEX[shape].aspect;
}

/** S/M/L pixel dims for a given button shape. */
export function shapeSizesFor(shape: ButtonShape): { S: SizeDims; M: SizeDims; L: SizeDims } {
  return SHAPE_INDEX[shape].presets;
}

/** Enforce a shape's aspect rule on a candidate (w, h) during freeform
 *  drag-resize. Returns the corrected (w, h). Used by EditableItem. */
export function applyAspectRule(
  shape: ButtonShape,
  w: number,
  h: number,
  dominantAxis: 'w' | 'h',
): { w: number; h: number } {
  const rule = shapeAspectRule(shape);
  if (rule.kind === 'square') {
    const both = Math.min(w, h);
    return { w: both, h: both };
  }
  if (rule.kind === 'squareFloor') {
    // Floor at 1:1. Either landscape or portrait is fine; going below
    // parity in the direction the user is driving pulls the *other*
    // axis along at the floor. Concretely:
    //   - driving W down through H → H follows W down (both shrink).
    //   - driving H down through W → W follows H down (both shrink).
    //   - driving up when already the larger axis → no-op (constraint
    //     is a floor, not a ceiling).
    // The "user drives W, W ends up smaller than H" flow must NOT raise
    // W back to H — that traps the user at their last-touched value
    // when they try to type a smaller number.
    if (dominantAxis === 'w' && w < h) return { w, h: w };
    if (dominantAxis === 'h' && h < w) return { w: h, h };
    return { w, h };
  }
  if (rule.kind === 'minRatio') {
    // Enforce w/h >= ratio. If current aspect drops below, adjust the
    // non-dominant axis (the one the user isn't actively driving) up so
    // the constraint holds.
    if (w / h < rule.ratio) {
      if (dominantAxis === 'w') {
        return { w, h: w / rule.ratio };
      }
      return { w: h * rule.ratio, h };
    }
    return { w, h };
  }
  return { w, h };
}

/** Generate a CSS clip-path polygon expression for a polygon-family shape.
 *  Returns null for rect/curve families (they use border-radius instead).
 *  Coordinates are percentages so the same expression scales to any
 *  (w, h). */
export function clipPathFor(shape: ButtonShape): string | null {
  switch (shape) {
    case 'triangle':
      // Equilateral, point up. Tip at (50, 0); base corners near the bottom.
      return 'polygon(50% 0%, 100% 100%, 0% 100%)';
    case 'diamond':
      // Rhombus, taller than wide — the taper is baked into the clip so
      // it reads as a diamond rather than a rotated square regardless of
      // the (square) bbox.
      return 'polygon(50% 0%, 90% 50%, 50% 100%, 10% 50%)';
    case 'pentagon':
      // Point up. Approx regular pentagon vertices.
      return 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)';
    case 'hexagon':
      // Pointy-top hex.
      return 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';
    case 'octagon': {
      // 8 vertices: 8 evenly-spaced points on a circumscribed circle,
      // rendered as percentages of the bbox. cos/sin for angle θ =
      // 22.5° + n*45°, with y flipped.
      const pts: string[] = [];
      for (let i = 0; i < 8; i++) {
        const theta = ((i * 45 + 22.5) * Math.PI) / 180;
        const x = 50 + 50 * Math.cos(theta);
        const y = 50 - 50 * Math.sin(theta);
        pts.push(`${x.toFixed(2)}% ${y.toFixed(2)}%`);
      }
      return `polygon(${pts.join(', ')})`;
    }
    case 'arrow':
      // Points right. Stem is 60% wide, head is 40% wide and full-height
      // triangle tapering to a point on the right edge.
      return 'polygon(0% 25%, 60% 25%, 60% 0%, 100% 50%, 60% 100%, 60% 75%, 0% 75%)';
    case 'chevron':
      // Right-pointing chevron (outlined tip — a > shape, thick).
      return 'polygon(0% 15%, 40% 15%, 100% 50%, 40% 85%, 0% 85%, 60% 50%)';
    default:
      return null;
  }
}

/** All shape ids in library order — for iterating in the picker UI. */
export const ALL_SHAPES: ButtonShape[] = SHAPE_LIBRARY.map((s) => s.id);
